import React, { useState } from "react";
import { Button as RawButton } from "@/components/ui/button";
import { Card as RawCard } from "@/components/ui/card";
import { Label as RawLable } from "@/components/ui/labe";
import {
  ChevronLeft,
  Calendar,
  Users,
  Clock,
  Ship,
  ArrowRight,
  CheckCircle,
} from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";

/**
 * @typedef {Object} Ferry
 * @property {number} base_price_per_passenger
 * @property {string} [name]
 * @property {string} [route]
 *
 * @typedef {Object} BookingData
 * @property {Date|string} date
 * @property {Date|string|null} [returnDate]
 * @property {number} passengers
 * @property {string} departureTime
 * @property {string|null} [returnDepartureTime]
 * @property {string} [vehicleType]
 * @property {boolean} isRoundTrip
 *
 * @typedef {Object} PassportDetails
 * @property {string} full_name_on_passport
 * @property {string} passport_number
 * @property {string} nationality
 * @property {string} passport_expiry_date
 *
 * @typedef {Object} FerryCheckoutViewProps
 * @property {Ferry} ferry
 * @property {BookingData} bookingData
 * @property {() => void} onBack
 * @property {(data: any) => void} onConfirm
 * @property {() => void} [onReturnToMenu]
 */

/**
 * @param {FerryCheckoutViewProps} props
 */
export default function FerryCheckoutView({
  ferry,
  bookingData,
  onBack,
  onConfirm,
  onReturnToMenu,
}) {
  const [usePassport, setUsePassport] = useState(true);
  const [passportDetails, setPassportDetails] = useState(
    /** @type {PassportDetails | null} */ (null),
  );

  const {
    date,
    returnDate,
    passengers,
    departureTime,
    returnDepartureTime,
    vehicleType,
    isRoundTrip,
  } = bookingData;

  const vehiclePrices = {
    none: 0,
    car: 150,
    motorbike: 50,
  };

  const vehicleTypeKey = /** @type {"none"|"car"|"motorbike"} */ (
    vehicleType || "none"
  );
  const passengerTotal =
    ferry.base_price_per_passenger * passengers * (isRoundTrip ? 2 : 1);
  const vehicleTotal = vehiclePrices[vehicleTypeKey] * (isRoundTrip ? 2 : 1);
  const totalAmount = passengerTotal + vehicleTotal;

  React.useEffect(() => {
    const loadPassport = async () => {
      //   try {
      //     const user = await base44.auth.me();
      //     if (user?.passport_details?.passport_number) {
      //       setPassportDetails(user.passport_details);
      //     }
      //   } catch (error) {
      //     console.error("Error loading passport:", error);
      //   }
    };
    loadPassport();
  }, []);

  const handleConfirm = () => {
    onConfirm({
      ferry,
      date,
      returnDate: isRoundTrip ? returnDate : null,
      departureTime,
      returnDepartureTime: isRoundTrip ? returnDepartureTime : null,
      passengers,
      vehicleType,
      isRoundTrip,
      totalAmount,
      passportDetails: usePassport && passportDetails ? passportDetails : null,
    });
  };

  const Button = /** @type {React.ComponentType<any>} */ (RawButton);
  const Card = /** @type {React.ComponentType<any>} */ (RawCard);
  const Label = /** @type {React.ComponentType<any>} */ (RawLable);
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={onBack} className="gap-2 -ml-2 text-[#0B2421] hover:bg-transparent hover:text-[#0B2421]/70 hover:underline">
            <ChevronLeft className="h-4 w-4" />
            Back to Ferry
          </Button>
          {onReturnToMenu && (
            <Button
              variant="outline"
              onClick={onReturnToMenu}
              className="gap-2 bg-[#0B2421] hover:bg-[#0B2421]/85 text-white border-none"
            >
              Return to Booking Menu
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold text-[#0B2421] mb-2">
            Confirm Ferry Selection
          </h1>
          <p className="text-gray-500 mb-8">
            Review your booking details
          </p>
        </motion.div>

        <div className="max-w-2xl mx-auto">
          <motion.div
            className="text-gray-600 bg-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="p-8 bg-white border border-gray-100 rounded-2xl shadow-sm">
              <h2 className="text-xl font-bold text-[#0B2421] mb-6">
                Passenger Information
              </h2>

              {/* Passport Section */}
              {passportDetails ? (
                <div className="mb-6 p-4 bg-gray-50 border border-gray-100 rounded-xl">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h3 className="font-semibold text-[#0B2421] mb-1">
                        Passport on File
                      </h3>
                      <p className="text-sm text-[#D4AF37]">
                        We found saved passport details for international
                        travel.
                      </p>
                    </div>
                    <Label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={usePassport}
                        onChange={(e) => setUsePassport(e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Use</span>
                    </Label>
                  </div>
                  {usePassport && (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Name:</span>
                        <span className="ml-2 font-medium">
                          {passportDetails.full_name_on_passport}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Passport:</span>
                        <span className="ml-2 font-medium">
                          {passportDetails.passport_number}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Nationality:</span>
                        <span className="ml-2 font-medium">
                          {passportDetails.nationality}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Expires:</span>
                        <span className="ml-2 font-medium">
                          {format(
                            new Date(passportDetails.passport_expiry_date),
                            "MMM yyyy",
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mb-6 p-4 border border-amber-200 bg-amber-50 rounded-xl">
                  <p className="text-sm text-amber-700">
                    💡 Tip: Save your passport details in your profile for
                    faster booking next time.
                  </p>
                </div>
              )}

              <h2 className="text-xl font-bold text-[#0B2421] mb-6 mt-6">
                Booking Summary
              </h2>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                    <img
                      src={ferry.image}
                      alt={ferry.operator}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-sm text-[#D4AF37] mb-1">
                      <Ship className="h-3 w-3" />
                      <span>{ferry.operator}</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs font-medium text-[#0B2421]">
                      <span>{ferry.origin_port}</span>
                      <ArrowRight className="h-3 w-3" />
                      <span>{ferry.destination_port}</span>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100 space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-gray-600">Trip Type:</span>
                    <span className="font-medium text-[#0B2421] ml-auto">
                      {isRoundTrip ? "Round-Trip" : "One-Way"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-gray-600">Departure Date:</span>
                    <span className="font-medium text-[#0B2421] ml-auto">
                      {format(date, "dd MMM yyyy")}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-gray-600">Departure Time:</span>
                    <span className="font-medium text-[#0B2421] ml-auto">
                      {departureTime}
                    </span>
                  </div>
                  {isRoundTrip && (
                    <>
                      <div className="flex items-center gap-3 text-sm">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-gray-600">Return Date:</span>
                        <span className="font-medium text-[#0B2421] ml-auto">
                          {format(returnDate, "dd MMM yyyy")}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-gray-600">Return Time:</span>
                        <span className="font-medium text-[#0B2421] ml-auto">
                          {returnDepartureTime}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="flex items-center gap-3 text-sm">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-gray-600">Passengers:</span>
                    <span className="font-medium text-[#0B2421] ml-auto">
                      {passengers}
                    </span>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {isRoundTrip
                        ? `Passengers (${passengers} × 2 trips)`
                        : `Passengers (${passengers})`}
                    </span>
                    <span className="font-medium text-[#0B2421]">
                      SGD {passengerTotal}
                    </span>
                  </div>
                  {vehicleType && vehicleType !== "none" && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 capitalize">
                        {isRoundTrip
                          ? `${vehicleType} (Round-Trip)`
                          : vehicleType}
                      </span>
                      <span className="font-medium text-[#0B2421]">
                        SGD {vehicleTotal}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-3 border-t border-gray-100">
                    <span className="text-[#0B2421]">Subtotal</span>
                    <span className="text-[#D4AF37]">
                      SGD {totalAmount.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border border-amber-200 bg-amber-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-amber-700">
                  💡 Payment will be processed after you complete selecting all
                  services
                </p>
              </div>

              <Button
                onClick={handleConfirm}
                className="w-full h-14 bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold text-lg"
              >
                <CheckCircle className="h-5 w-5 mr-2" />
                Confirm Ferry Selection
              </Button>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
